import React, { useMemo, useState } from 'react';
import PreviewCanvas from './PreviewCanvas';
import AiBanner from './AiBanner';
import { useBlockApply } from './useBlockApply';
import './effectBlocks.css';

// AI-rankable named looks; each is just a bundle of the slider values.
const PRESETS = [
    { key: 'cinematic-warm', label: 'Cinematic warm', warmth: 0.45, saturation: 1.12, brightness: 0.03, grayscale: false },
    { key: 'punchy', label: 'Punchy & bright', warmth: 0.1, saturation: 1.35, brightness: 0.08, grayscale: false },
    { key: 'moody-teal', label: 'Moody teal', warmth: -0.5, saturation: 0.9, brightness: -0.04, grayscale: false },
    { key: 'vintage', label: 'Vintage', warmth: 0.3, saturation: 0.75, brightness: -0.05, grayscale: false },
    { key: 'bw', label: 'B&W', warmth: 0, saturation: 1, brightness: 0, grayscale: true },
];

const DEFAULT = { preset: 'cinematic-warm', warmth: 0.45, saturation: 1.12, brightness: 0.03, grayscale: false };

/**
 * LookBlockEditor — panel block: a color grade -> colorchannelmixer_tool +
 * hue_tool. Pick an AI-suggested preset then fine-tune warmth / saturation /
 * brightness; the preview approximates the grade with a CSS filter.
 */
function LookBlockEditor({ videoWorkspaceId, frameUrl, sourceFileName, mediaInfo, aiSuggestion, onApplied, onClose }) {
    const { setMeasured, aspectRatio, ready, applying, error, apply } =
        useBlockApply({ videoWorkspaceId, sourceFileName, mediaInfo });
    const [look, setLook] = useState(() => (
        aiSuggestion?.params
            ? { ...DEFAULT, ...aiSuggestion.params, preset: 'custom' }
            : DEFAULT
    ));

    const update = (patch) => setLook((prev) => ({ ...prev, ...patch, preset: 'custom' }));
    const applyPreset = (p) => setLook({ preset: p.key, warmth: p.warmth, saturation: p.saturation, brightness: p.brightness, grayscale: p.grayscale });

    // CSS approximation of the grade for the preview: saturate/brightness, plus a
    // sepia tint for warmth or a hue-rotate for cool.
    const frameFilter = useMemo(() => {
        const sat = look.grayscale ? 0 : look.saturation;
        const parts = [`saturate(${sat})`, `brightness(${1 + look.brightness})`];
        if (look.warmth > 0.02) parts.push(`sepia(${Math.min(0.6, look.warmth * 0.5)})`);
        else if (look.warmth < -0.02) parts.push(`hue-rotate(${Math.round(look.warmth * 25)}deg)`);
        return parts.join(' ');
    }, [look]);

    const isNoop = !look.grayscale
        && Math.abs(look.warmth) < 0.005
        && Math.abs(look.saturation - 1) < 0.005
        && Math.abs(look.brightness) < 0.005;

    return (
        <div className="eb-editor">
            <AiBanner rationale={aiSuggestion?.rationale} />
            <div className="eb-editor-grid">
                <PreviewCanvas
                    frameUrl={frameUrl}
                    isVideo
                    aspectRatio={aspectRatio}
                    blocks={[]}
                    onMeta={setMeasured}
                    onSelect={() => {}}
                    onChange={() => {}}
                    frameFilter={frameFilter}
                />
                <div className="eb-controls">
                    <h5>Color look</h5>

                    <div className="eb-look-presets">
                        {PRESETS.map((p) => (
                            <button
                                key={p.key}
                                type="button"
                                className={`eb-chip${look.preset === p.key ? ' on' : ''}`}
                                onClick={() => applyPreset(p)}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>

                    <label className="eb-ctl">
                        <span>Warmth <em>{Math.round(look.warmth * 100)}</em></span>
                        <input type="range" min="-100" max="100" step="1"
                            value={Math.round(look.warmth * 100)}
                            onChange={(e) => update({ warmth: Number(e.target.value) / 100 })} />
                    </label>
                    <label className="eb-ctl">
                        <span>Saturation <em>{Math.round(look.saturation * 100)}%</em></span>
                        <input type="range" min="0" max="200" step="1"
                            disabled={look.grayscale}
                            value={Math.round(look.saturation * 100)}
                            onChange={(e) => update({ saturation: Number(e.target.value) / 100 })} />
                    </label>
                    <label className="eb-ctl">
                        <span>Brightness <em>{Math.round(look.brightness * 100)}</em></span>
                        <input type="range" min="-30" max="30" step="1"
                            value={Math.round(look.brightness * 100)}
                            onChange={(e) => update({ brightness: Number(e.target.value) / 100 })} />
                    </label>
                    <label className="eb-check">
                        <input type="checkbox" checked={!!look.grayscale} onChange={(e) => update({ grayscale: e.target.checked })} />
                        <span>Black &amp; white</span>
                    </label>

                    {error && <p className="eb-error">{error}</p>}

                    <div className="eb-actions">
                        {onClose && <button type="button" className="eb-btn" onClick={onClose}>Cancel</button>}
                        <button
                            type="button"
                            className="eb-btn primary"
                            disabled={!ready || applying || isNoop}
                            onClick={() => apply({ type: 'look', params: look }, onApplied)}
                        >
                            {applying ? 'Applying…' : 'Add to timeline'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LookBlockEditor;
